import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFileExcel } from 'react-icons/fa';
import { showCurrency } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import { useTrailNavigate } from '../../context/EntityTrailContext';
import { ContractDeletionModal } from '../../components/CascadeDeletionModal';
import { Section, StatStrip, DataTable, IdChip, RowActionsMenu, Button, ExportModal, buildXlsxExport, TrailLink } from '../../components/EntityList';
import { useClientSort } from '../../hooks/useClientSort';

function formatDate(date) {
	return date ? new Date(date).toLocaleDateString('es-UY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function AssetContracts({ assetId, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const navigateWithTrail = useTrailNavigate();
	const [deletingContract, setDeletingContract] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'id',
		accessors: { id: (contract) => contract.customId },
	});

	const handleEditContract = (contract) => {
		navigate(`/AdminScreen/assets/asset/${assetId}/contracts/${contract._id}/edit`);
	};

	const stats = useMemo(() => {
		const totalByCurrency = new Map();
		rows.forEach((contract) => {
			const key = contract.currency || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (contract.amount || 0));
		});
		return [
			{ label: t('entity.contract.plural'), value: rows.length },
			{
				label: t('field.amount'),
				value: [...totalByCurrency.entries()].map(([currency, total]) => formatMoney(currency, total)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows, t]);


	const columns = [
		{ key: 'id', header: 'ID', sortable: true, render: (contract) => <IdChip>{contract.customId}</IdChip> },
		{ key: 'currency', header: t('field.currency'), className: 'tw:font-mono', render: (contract) => contract.currency },
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (contract) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(contract.currency, contract.amount)}</span>,
		},
		{ key: 'periodInit', header: t('field.periodInit'), render: (contract) => formatDate(contract.periodInit) },
		{ key: 'periodEnd', header: t('field.periodEnd'), render: (contract) => formatDate(contract.periodEnd) },
		{
			key: 'tenant',
			header: t('entity.tenant'),
			render: (contract) =>
				contract.owner ? (
					<TrailLink to={`/AdminScreen/accounts/account/${contract.owner._id || contract.owner}`} onClick={(e) => e.stopPropagation()}>
						{contract.owner.name}
					</TrailLink>
				) : null,
		},
		{ key: 'folder', header: t('field.folder'), render: (contract) => contract.folder },
	];

	const exportFields = [
		{ key: 'id', label: 'ID', locked: true },
		{ key: 'currency', label: t('field.currency') },
		{ key: 'amount', label: t('field.amount') },
		{ key: 'periodInit', label: t('field.periodInit') },
		{ key: 'periodEnd', label: t('field.periodEnd') },
		{ key: 'tenant', label: t('entity.tenant') },
		{ key: 'folder', label: t('field.folder') },
	];

	const exportGetters = {
		id: (contract) => contract.customId || '',
		currency: (contract) => contract.currency || '',
		amount: (contract) => formatMoney(contract.currency, contract.amount),
		periodInit: (contract) => formatDate(contract.periodInit),
		periodEnd: (contract) => formatDate(contract.periodEnd),
		tenant: (contract) => contract.owner?.name || '',
		folder: (contract) => contract.folder || '',
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: sortedRows,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Alquileres',
			filePrefix: 'alquileres',
		});

	const renderRowActions = (contract) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => handleEditContract(contract) },
				{ label: t('action.delete'), onClick: () => setDeletingContract(contract), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={rows.length === 0}>
					{t('action.export')}
				</Button>
				<Button size='sm' variant='primary' onClick={() => navigate(`/AdminScreen/assets/asset/${assetId}/newContract`)}>
					{t('action.add')} {t('entity.contract')}
				</Button>
			</div>
			<Section title={t('entity.contract.plural')}>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					onRowClick={(contract) => navigateWithTrail(`/AdminScreen/contracts/contract/${contract._id}`)}
					rowActions={renderRowActions}
					emptyMessage={t('msg.noData')}
				/>
			</Section>

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={rows.length}
				fields={exportFields}
				subtitle={t('entity.contract.plural')}
				onExport={handleExport}
			/>

			<ContractDeletionModal
				show={Boolean(deletingContract)}
				record={deletingContract}
				onClose={() => setDeletingContract(null)}
				onDeleted={() => {
					setDeletingContract(null);
					onRefetch();
				}}
			/>
		</div>
	);
}

export default AssetContracts;
